import React from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { api } from '../lib/api';
import { AsanohaBackground, SectionHeading, InstructorCard, TestimonialCard, FAQ, Btn, Badge, languageMeta } from '../components';
import { taughtLanguages } from './Marketplace';
import { palette as T, alpha, type } from '../design';

// ropida/home.jsx — Homepage (Terracotta Studio)


function HeroDecoration() {
  return (
    <div style={{ position: 'relative', width: '100%', height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', background: alpha('terracotta', 'tint') }} />
      <div style={{ position: 'absolute', width: 160, height: 160, borderRadius: '50%', background: alpha('caramelGold', 'tint'), transform: 'translate(30px, -20px)' }} />
      <div style={{ position: 'absolute', width: 120, height: 120, borderRadius: '50%', border: '2px solid ' + alpha('terracotta', 'tint') }} />
      <div style={{ position: 'absolute', width: 80, height: 80, borderRadius: '50%', background: alpha('terracotta', 'tint'), transform: 'translate(-40px, 40px)' }} />
    </div>
  );
}

function Step({ num, title, desc }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', position: 'relative', flex: 1 }}>
      <div style={{
        width: 48, height: 48, borderRadius: '50%',
        background: T.white, border: '2px solid ' + T.terracotta,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.lead, color: T.terracotta,
        marginBottom: 16, position: 'relative', zIndex: 1
      }}>
        {num}
      </div>
      <h4 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: 15, color: T.espresso, margin: '0 0 8px' }}>{title}</h4>
      <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0, lineHeight: 1.6, maxWidth: 200 }}>{desc}</p>
    </div>
  );
}

// "Japanese", "Japanese or Mandarin", "Japanese, Korean or Mandarin".
function joinWithOr(words) {
  if (words.length <= 1) return words[0] || '';
  return words.slice(0, -1).join(', ') + ' or ' + words[words.length - 1];
}

// "Good luck!" in each taught language, for the closing banner. A language without an entry
// simply adds no phrase.
const CHEER = { JP: 'がんばって！', ZH: '加油！' };

function HomePage({ setPage }) {
  const isMobile = useIsMobile();
  const SP = isMobile ? '48px 16px' : '80px 32px';
  const [instructors, setInstructors] = React.useState([]);
  const [languages, setLanguages] = React.useState([]);
  const [instructorsLoading, setInstructorsLoading] = React.useState(true);

  React.useEffect(() => {
    api.get('/instructors').then(data => {
      if (!data?.instructors) return;
      setInstructors(data.instructors.slice(0, 4));
      // From the whole roster, not the four featured: a language is offered once anyone teaches it.
      setLanguages(taughtLanguages(data.instructors));
    }).catch(() => {}).finally(() => setInstructorsLoading(false));
  }, []);

  // The hero names only languages a listed instructor teaches (FB-5). Until the roster loads, or
  // when nobody is listed, it promises no particular language.
  const languageNames = joinWithOr(languages.map(code => languageMeta(code).label));
  const cheer = languages.map(code => CHEER[code]).filter(Boolean).join('');

  const faqItems = [
    { q: 'Are instructors vetted?', a: 'Yes. We don\'t have open signups for instructors. Every teacher on Ropida goes through an internal onboarding process that includes a teaching demo, credential verification, and a trial period.' },
    { q: 'What happens after my first lesson?', a: 'You can book more of your instructor\'s upcoming sessions — 1-on-1 or small group. There\'s no subscription — no surprises.' },
    { q: 'What platform do classes happen on?', a: 'Whatever your instructor uses — Zoom, Google Meet, or Teams. The meeting link appears in your dashboard (and by email) once your booking is confirmed.' },
    { q: 'Can I switch instructors?', a: 'Absolutely. If you don\'t click with your first instructor, we\'ll help you find a better match. Your happiness with the process matters more than a single booking.' },
  ];

  const valueProps = [
    { title: 'Teachers, not chatbots', desc: 'Real native instructors, hand-picked and onboarded by us. No self-serve signups — every teacher is vetted so you can trust who you\'re learning from.' },
    { title: 'Your pace, your schedule', desc: 'Instructors publish their upcoming sessions — pick the ones that fit your week, shown in your own timezone.' },
    { title: 'Pay in your currency', desc: '₱ PHP, $ USD, RM MYR, and more — auto-detected. No confusion, no mental math. Just learn.' },
  ];

  return (
    <div>
      {/* ── Hero ── */}
      <AsanohaBackground style={{ paddingTop: isMobile ? 100 : 120, paddingBottom: isMobile ? 56 : 90 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: isMobile ? '0 16px' : '0 32px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 32 : 48, alignItems: 'center' }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <Badge variant="terracotta">Vetted native instructors</Badge>
            </div>
            <h1 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? 'clamp(36px, 10vw, 48px)' : 'clamp(44px, 4.5vw, 60px)', color: T.espresso, lineHeight: 1.08, margin: '0 0 20px', letterSpacing: -0.5 }}>
              Find your <span style={{ color: T.terracotta }}>language teacher.</span>
            </h1>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? 15 : 17, color: T.walnut, lineHeight: 1.65, margin: '0 0 28px', maxWidth: 480 }}>
              Learn {languageNames || 'a new language'} with vetted native instructors — we'll help you find a teacher you'll actually love.
            </p>
            <Btn onClick={() => setPage('marketplace')} size={isMobile ? 'md' : 'lg'}>Find an instructor →</Btn>
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <HeroDecoration />
            </div>
          )}
        </div>
      </AsanohaBackground>

      {/* ── Trust bar ── */}
      <div style={{ background: T.warmGrey, borderTop: '1px solid ' + T.border, borderBottom: '1px solid ' + T.border, padding: '16px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', justifyContent: 'center', gap: isMobile ? 16 : 40, flexWrap: 'wrap' }}>
          {['Vetted instructors', 'No subscription', 'Flexible scheduling'].map(item => (
            <span key={item} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.label, fontWeight: 600, color: T.driftwood, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── Why Ropida ── */}
      <div style={{ background: T.white, padding: SP }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <SectionHeading eyebrow="Why Ropida" title="Built for serious learners" subtitle="No gimmicks. No chatbots. Just real teachers and a platform that respects your time." center />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? 16 : 24, marginTop: isMobile ? 24 : 40 }}>
            {valueProps.map((card, i) => (
              <div key={card.title} style={{ background: T.ivory, borderRadius: 16, padding: '28px 24px', border: '1px solid ' + T.border }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, background: T.terracotta,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: type.lead, color: T.white,
                  marginBottom: 16
                }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600, fontSize: 17, color: T.espresso, margin: '0 0 8px' }}>{card.title}</h3>
                <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.walnut, margin: 0, lineHeight: 1.65 }}>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Featured Instructors ── */}
      <div style={{ background: T.ivory, padding: SP }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: isMobile ? 24 : 40, flexWrap: 'wrap', gap: 16 }}>
            <SectionHeading eyebrow="Meet our team" subtitle="Hand-picked native instructors ready to guide your journey." />
            <Btn variant="ghost" onClick={() => setPage('marketplace')} size="sm">See all teachers →</Btn>
          </div>
          {instructorsLoading ? (
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: type.body, color: T.driftwood, textAlign: 'center' }}>Loading featured instructors…</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: isMobile ? 16 : 24 }}>
              {instructors.map(ins => (
                <InstructorCard key={ins.id} instructor={ins} onClick={() => setPage('instructor-profile-' + (ins.slug || ins.id))} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── How it works ── */}
      <div style={{ background: T.warmGrey, padding: SP }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <SectionHeading eyebrow="Simple process" title="Four steps to fluency" center />
          <div style={{ position: 'relative', marginTop: isMobile ? 24 : 48 }}>
            {!isMobile && (
              <div style={{ position: 'absolute', top: 24, left: '12.5%', right: '12.5%', height: 2, background: T.border, zIndex: 0 }} />
            )}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? 24 : 32, position: 'relative', zIndex: 1 }}>
              <Step num={1} title="Find your teacher" desc="Browse profiles, filter by language, level, and specialties." />
              <Step num={2} title="Book a session" desc="Pick one of their upcoming sessions — times shown in your timezone." />
              <Step num={3} title="Meet online" desc="Have your first class. It's as warm as meeting a friend." />
              <Step num={4} title="Keep going" desc="Book their next sessions whenever you're ready." />
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: isMobile ? 32 : 48 }}>
            <Btn onClick={() => setPage('how-it-works')}>Learn more →</Btn>
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={{ background: T.white, padding: SP }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <SectionHeading eyebrow="Quick answers" title="Common questions" center />
          <div style={{ marginTop: isMobile ? 24 : 40 }}>
            <FAQ items={faqItems} />
          </div>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Btn variant="ghost" onClick={() => setPage('contact')}>More questions? Talk to us →</Btn>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div style={{ background: T.terracotta, padding: SP, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 240, height: 240, borderRadius: '50%', background: alpha('white', 'hover') }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 180, height: 180, borderRadius: '50%', background: alpha('white', 'hover') }} />
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {cheer && <p style={{ fontFamily: "'Noto Serif JP', serif", fontSize: isMobile ? type.lead : type.h3, color: T.white, marginBottom: 8 }}>{cheer}</p>}
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 700, fontSize: isMobile ? type.h2 : type.h1, color: T.white, margin: '0 0 16px', lineHeight: 1.1 }}>
            Start your language journey today.
          </h2>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: isMobile ? type.body : type.lead, color: T.white, marginBottom: 28, lineHeight: 1.6 }}>
            No subscription, no commitment. Just you, your teacher, and a language that opens new worlds.
          </p>
          <Btn variant="white" size={isMobile ? 'md' : 'lg'} onClick={() => setPage('marketplace')}>Find your sensei →</Btn>
        </div>
      </div>
    </div>
  );
}

export { HomePage };
